import {
  IAssessmentFrameworkDimensionDetail,
  IAssessmentPartition,
  IQuadrant,
} from '../models/assessment-framework';

export function collectDefinedFrameworkParameterCodes(
  parameters: IAssessmentFrameworkDimensionDetail[] | undefined,
): string[] {
  return (parameters || [])
    .map((param) => param.code)
    .filter((code): code is string => typeof code === 'string' && code !== '');
}

/** Exact string match against framework standard/primary/secondary parameter codes. */
export function isDefinedFrameworkParameterCode(
  paramCode: string | null | undefined,
  definedCodes: readonly string[],
): boolean {
  if (typeof paramCode !== 'string' || paramCode === '') {
    return false;
  }
  return definedCodes.includes(paramCode);
}

export function quadrantHasInvalidConstraintParams(
  quadrant: IQuadrant,
  definedCodes: readonly string[],
): boolean {
  const intersectionInvalid = (quadrant.constraints || []).some(
    (constraint) => !isDefinedFrameworkParameterCode(constraint.param, definedCodes),
  );
  const testletInvalid = (quadrant.config?.constraints || []).some(
    (constraint) => !isDefinedFrameworkParameterCode(constraint.config?.param, definedCodes),
  );
  return intersectionInvalid || testletInvalid;
}

export function countInvalidQuadrantConstraintParams(
  quadrants: IQuadrant[] | undefined,
  definedCodes: readonly string[],
): number {
  let count = 0;
  for (const quadrant of quadrants || []) {
    for (const constraint of quadrant.constraints || []) {
      if (!isDefinedFrameworkParameterCode(constraint.param, definedCodes)) {
        count++;
      }
    }
    for (const constraint of quadrant.config?.constraints || []) {
      if (!isDefinedFrameworkParameterCode(constraint.config?.param, definedCodes)) {
        count++;
      }
    }
  }
  return count;
}

export function compareQuadrantsBySection(
  a: IQuadrant,
  b: IQuadrant,
  partitions: IAssessmentPartition[] | undefined,
): number {
  const sectionA = (partitions || []).findIndex((p) => `${p.id}` === `${a.section}`);
  const sectionB = (partitions || []).findIndex((p) => `${p.id}` === `${b.section}`);
  if (sectionA !== sectionB) {
    return sectionA - sectionB;
  }
  return +a.id - +b.id;
}

export function sortQuadrantsBySection<T extends IQuadrant>(
  quadrants: T[] | undefined,
  partitions: IAssessmentPartition[] | undefined,
): T[] {
  return [...(quadrants || [])].sort((a, b) => compareQuadrantsBySection(a, b, partitions));
}

export function sortQuadrantsBySectionInPlace(
  quadrants: IQuadrant[] | undefined,
  partitions: IAssessmentPartition[] | undefined,
): void {
  if (!quadrants?.length) {
    return;
  }
  quadrants.sort((a, b) => compareQuadrantsBySection(a, b, partitions));
}

export function findPartitionForSection(
  partitions: IAssessmentPartition[] | undefined,
  sectionId: string | number | null | undefined,
): IAssessmentPartition | undefined {
  if (sectionId == null || sectionId === '') {
    return undefined;
  }
  return (partitions || []).find((partition) => `${partition.id}` === `${sectionId}`);
}

export function isValidQuadrantSection(
  partitions: IAssessmentPartition[] | undefined,
  sectionId: string | number | null | undefined,
): boolean {
  return !!findPartitionForSection(partitions, sectionId);
}

export function getQuadrantSectionDisplayName(
  partitions: IAssessmentPartition[] | undefined,
  sectionId: string | number | null | undefined,
): string {
  const partition = findPartitionForSection(partitions, sectionId);
  if (!partition) {
    if (sectionId == null || sectionId === '') {
      return '—';
    }
    return '(unknown section)';
  }
  return partition.description?.trim() || '—';
}

export function formatPartitionSectionOption(partition: IAssessmentPartition): string {
  const name = partition.description?.trim() || '(no name)';
  return `${partition.id} — ${name}`;
}
